/**
 * Declare a global function allowing template-level metadata to be captured
 * and used from external JavaScript to avoid inline scripts.
 */
(function (window) {
  window.captureMetadata = captureMetadata;

  /**
   * Capture all metadata elements whose `itemprop` attribute matches a name in
   * the first path segment and produce an object constructed from their path
   * segments and values, accepting an optional base object.
   *
   * @param {string} name - The top-level name of the object to be constructed.
   * This will be used to determine which meta elements should be picked up for
   * inclusion in the resulting object.
   * @param {object} base - An optional object to use as a base object to be
   * extended with the metadata found in the meta elements.
   * @returns {object}
   */
  function captureMetadata(name, base) {
    // Grab all meta tags with base path segment.
    const metaTags = document.querySelectorAll('meta[itemprop^="' + name + '."]');
    return Array.prototype.reduce.call(metaTags, reduceMetaTagsIntoObject, base || {});
  }

  /**
   * Given a NodeList of meta elements, produce an object with properties that
   * follow the paths given in each meta element’s `itemprop` attribute and
   * values according to the element’s `content` attribute.
   *
   * @param {object} base - An object to extend with a meta element’s data.
   * @param {Node} meta - A Node object representing a meta element.
   * @returns {object} A modified `base` object.
   */
  function reduceMetaTagsIntoObject(base, meta) {
    const segments = meta.getAttribute('itemprop').split('.');
    // Remove first segment, which is the top-level name segment.
    segments.shift();
    const firstSegment = segments.shift();
    assignValue(base, firstSegment, buildObjectFromSegments(segments, meta.getAttribute('content'), base[firstSegment]));

    return base;
  }

  /**
   * Build an object using an array of segments to determine its structure.
   *
   * @param {string[]} segments - An array of segments with which to construct
   * the object structure.
   * @param {string} value - The value to be assigned at the lowest property in
   * the segment chain.
   * @returns {object}
   */
  function buildObjectFromSegments(segments, value, existing) {
    if (segments.length) {
      // If there are segments left, we assisn a new object to it.
      const object = Object.assign({}, existing);
      const nextSegment = segments.shift();
      assignValue(object, nextSegment, buildObjectFromSegments(segments, value, object[nextSegment]));
      return object;
    } else {
      // There are no more segments, so we return processed value.
      return processValue(value);
    }
  }

  /**
   * Assign a value to an object property according to the property type.
   *
   * NOTE: This modifies the object in place.
   *
   * @param {object} object - The object to which a value should be appended.
   * @param {(string|object)} - The value to be assigned.
   * @param {string} segment - The property to which the value should be
   * assigned.
   * @returns null;
   */
  function assignValue(object, segment, value) {
    // If segment denotes an array, push the built object to it; otherwise
    // assign the value.
    if (segment.match(/\[\]$/)) {
      const cleanSegment = segment.replace(/\[\]$/, '');
      object[cleanSegment] = object[cleanSegment] || [];
      object[cleanSegment].push(value);
    } else {
      object[segment] = value;
    }
  }

  /**
   * Examine a value and process it before returning it.
   *
   * @param {string} value - The value to be examined.
   * @returns {(string|boolean)} Always the original string, except in the
   * following cases:
   * - If `value` is the literal string "true" or "false" (case insensitive), a
   *   boolean is returned.
   */
  function processValue(value) {
    let result;
    switch (value.toLowerCase()) {
      case 'true':
      case 'false':
        // Make `result` a boolean.
        result = value === 'true';
      default:
        // Let `result` be the original string.
        result = value;
    }
    return result;
  }
})(window);
